import React, { useCallback, useEffect } from "react";
import {
  NavigationContainer,
  DefaultTheme,
  DarkTheme,
} from "@react-navigation/native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { Platform, Linking, Dimensions } from "react-native";
//hi
import {
  useFonts,
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  Inter_700Bold,
  Inter_800ExtraBold,
} from "@expo-google-fonts/inter";
import * as SplashScreen from "expo-splash-screen";
import HomeScreen from "./src/screens/HomeScreen";
import OpportunityScreen from "./src/screens/OpportunityScreen";
import LoginScreenPage from "./src/screens/auth/LoginPage";
import SignUpScreenPage from "./src/screens/auth/SignUpPage";
import AboutScreen from "./src/screens/AboutScreen";
// Team page temporarily hidden
// import TeamScreen from "./src/screens/TeamScreen";
import ContactScreen from "./src/screens/ContactScreen";
import PrivacyPolicyScreen from "./src/screens/PrivacyPolicyScreen";
import LetterScreen from "./src/screens/LetterScreen";
import OrganizationDashboard from "./src/screens/OrganizationDashboard";
import SchoolAdminDashboard from "./src/screens/SchoolAdminDashboard";
import TeacherPortal from "./src/screens/TeacherPortal";
import StudentPortal from "./src/screens/StudentPortal";
import { AuthProvider } from "./src/contexts/AuthContext";
import { FavoritesProvider } from "./src/contexts/FavoritesContext";
import { ThemeProvider, useTheme } from "./src/contexts/ThemeContext";
// eslint-disable-next-line @typescript-eslint/no-var-requires
const Parse = require("parse");
import AsyncStorage from "@react-native-async-storage/async-storage";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import { navigationRef, RootStackParamList } from "./src/navigation";
import "react-native-url-polyfill/auto";

// Import web initialization for better cross-platform support
import "./src/services/webInit";

SplashScreen.preventAutoHideAsync();

// Use environment variables instead of hardcoded values
let PARSE_APPLICATION_ID: string;
let PARSE_JAVASCRIPT_KEY: string;
let PARSE_SERVER_URL: string;

try {
  // Try to import from @env first
  const env = require("@env");
  PARSE_APPLICATION_ID = env.PARSE_APP_ID;
  PARSE_JAVASCRIPT_KEY = env.PARSE_JS_KEY;
  PARSE_SERVER_URL = env.PARSE_SERVER_URL;
} catch (error) {
  console.warn("Could not load environment variables from @env, using fallback");
  // Fallback to process.env for web builds
  PARSE_APPLICATION_ID = process.env.PARSE_APP_ID || "1PZ54pDpq1AtWAWftgbgW0tup4oSSEks8AU7f6NE";
  PARSE_JAVASCRIPT_KEY = process.env.PARSE_JS_KEY || "L3QrAa7HXBIB9CNiVwT48p0T9mjz4pws29KqdOeM";
  PARSE_SERVER_URL = process.env.PARSE_SERVER_URL || "https://parseapi.back4app.com/";
}

// Initialize Parse with proper error handling
const initializeParse = () => {
  try {
    if (!PARSE_APPLICATION_ID || !PARSE_JAVASCRIPT_KEY || !PARSE_SERVER_URL) {
      console.error("Missing Parse configuration");
      return false;
    }

    // Check if already initialized
    if (Parse.applicationId === PARSE_APPLICATION_ID) {
      console.log("Parse already initialized");
      return true;
    }

    Parse.initialize(PARSE_APPLICATION_ID, PARSE_JAVASCRIPT_KEY);
    Parse.serverURL = PARSE_SERVER_URL;

    // Set AsyncStorage for React Native environments
    if (Platform.OS !== "web" && AsyncStorage) {
      Parse.setAsyncStorage(AsyncStorage);
    }

    console.log("Parse initialized successfully in App.tsx");
    console.log("Environment:", {
      platform: Platform.OS,
      appId: PARSE_APPLICATION_ID ? "SET" : "NOT SET",
      jsKey: PARSE_JAVASCRIPT_KEY ? "SET" : "NOT SET",
      serverUrl: PARSE_SERVER_URL ? "SET" : "NOT SET"
    });

    return true;
  } catch (error) {
    console.error("Error initializing Parse:", error);
    return false;
  }
};

// Initialize Parse
const parseInitialized = initializeParse();
if (!parseInitialized) {
  console.warn("Parse initialization failed - some features may not work");
}

const Stack = createNativeStackNavigator<RootStackParamList>();

// AppNavigation is needed to use theme context inside NavigationContainer
function AppNavigation() {
  const { theme } = useTheme();
  return (
    <NavigationContainer
      ref={navigationRef}
      theme={theme === "dark" ? DarkTheme : DefaultTheme}
      // ... all your existing linking setup ...
      linking={{
        prefixes: [
          "opportunitydb://",
          "https://opportunity-db.vercel.app",
          "https://opportunity-db-dev.vercel.app",
          "https://bcstem.ca",
          "http://bcstem.ca",
        ],

        config: {
          screens: {
            Home: "",
            // Two-segment path; matched before the single-segment StudentPortal
            // catch-all so /opportunity/<id> resolves to its own screen instead
            // of collapsing into Home (the old behavior that produced thin,
            // homepage-shaped opportunity pages for crawlers).
            Opportunity: "opportunity/:opportunityId",
            About: "about",
            // Team page temporarily hidden
            // Team: "team",
            Contact: "contact",
            Login: "login",
            SignUp: "signup",
            PrivacyPolicy: "privacy-policy",
            Letter: "letter",
            OrganizationDashboard: "organization/dashboard",
            SchoolAdminDashboard: "school-admin",
            TeacherPortal: "teacher-portal",
            StudentPortal: ":schoolSlug", // Matches any path not defined above (e.g., /seaquam)
          },
        },
        getInitialURL: async () => {
          const url = await Linking.getInitialURL();
          if (url) {
            console.log("App opened with URL:", url);
            if (url.includes("opportunity/")) {
              // Routed natively by linking.config to the Opportunity screen.
              // Manually navigating to Home here would collapse the URL to "/"
              // and render the thin homepage-shaped page crawlers used to see.
            } else {
              // Handle school portal URLs like bcstem.ca/seaquam
              const pathParts = url.split('/').filter(Boolean);
              const lastPart = pathParts[pathParts.length - 1];

              // Known routes to exclude
              const knownRoutes = ['about', 'team', 'contact', 'login', 'signup',
                'organization', 'school', 'privacy-policy', 'letter'];

              if (!knownRoutes.includes(lastPart) && !lastPart.includes('opportunity')) {
                // Assume it's a school slug
                setTimeout(() => {
                  navigationRef.current?.navigate('StudentPortal', { schoolSlug: lastPart });
                }, 100);
              }
            }
            return url;
          }
          return null;
        },
        subscribe: (listener) => {
          const linkingSubscription = Linking.addEventListener(
            "url",
            ({ url }) => {
              console.log("Incoming link event:", url);
              if (url.includes("opportunity/")) {
                // Routed natively by linking.config to the Opportunity screen.
              } else {
                // Handle school portal URLs
                const pathParts = url.split('/').filter(Boolean);
                const lastPart = pathParts[pathParts.length - 1];
                const knownRoutes = ['about', 'team', 'contact', 'login', 'signup',
                  'organization', 'school', 'privacy-policy', 'letter'];

                if (!knownRoutes.includes(lastPart) && !lastPart.includes('opportunity')) {
                  navigationRef.current?.navigate('StudentPortal', { schoolSlug: lastPart });
                }
              }
              listener(url);
            },
          );
          return () => {
            linkingSubscription.remove();
          };
        },
      }}
      data-oid="0xo54.2"
    >
      <Stack.Navigator
        initialRouteName="Home"
        screenOptions={{
          headerShown: false,
        }}
        data-oid="uszw0zq"
      >
        <Stack.Screen
          name="Home"
          component={HomeScreen}
          options={{ title: 'BC STEM Opportunities | Discover STEM Programs in BC' }}
          data-oid="6raocxv"
        />
        <Stack.Screen
          name="Opportunity"
          component={OpportunityScreen}
        />
        <Stack.Screen
          name="Login"
          component={LoginScreenPage}
          data-oid="7ofkmju"
        />

        <Stack.Screen
          name="SignUp"
          component={SignUpScreenPage}
          data-oid="2uik2vz"
        />

        {/* Titles must match scripts/generate-static.js. React Navigation sets
            document.title from the route name by default, so without these the
            rendered <title> Google indexes collapsed to bare "About"/"Contact",
            overwriting the descriptive title in the static HTML. */}
        <Stack.Screen
          name="About"
          component={AboutScreen}
          options={{ title: 'About | BC STEM Opportunities' }}
          data-oid="cfx2arm"
        />
        {/* Team page temporarily hidden
        <Stack.Screen name="Team" component={TeamScreen} />
        */}
        <Stack.Screen
          name="Contact"
          component={ContactScreen}
          options={{ title: 'Contact | BC STEM Opportunities' }}
        />
        <Stack.Screen
          name="PrivacyPolicy"
          component={PrivacyPolicyScreen}
          options={{ title: 'Privacy Policy | BC STEM Opportunities' }}
        />
        <Stack.Screen name="Letter" component={LetterScreen} />
        <Stack.Screen name="OrganizationDashboard" component={OrganizationDashboard} />
        <Stack.Screen name="SchoolAdminDashboard" component={SchoolAdminDashboard} />
        <Stack.Screen name="TeacherPortal" component={TeacherPortal} />
        <Stack.Screen name="StudentPortal" component={StudentPortal} />
      </Stack.Navigator>
    </NavigationContainer>
  );
}

export default function App() {
  const [fontsLoaded, fontError] = useFonts({
    "Inter-Regular": Inter_400Regular,
    "Inter-Medium": Inter_500Medium,
    "Inter-SemiBold": Inter_600SemiBold,
    "Inter-Bold": Inter_700Bold,
    "Inter-ExtraBold": Inter_800ExtraBold,
  });

  const onLayoutRootView = useCallback(async () => {
    if (fontsLoaded || fontError) {
      await SplashScreen.hideAsync();
    }
  }, [fontsLoaded, fontError]);

  console.log(process.env.ANALYTIC_KEY);

  useEffect(() => {
    if (Platform.OS === "web") {
      const gaScript = document.createElement("script");
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${process.env.ANALYTIC_KEY}`;
      document.head.appendChild(gaScript);

      const gaConfigScript = document.createElement("script");
      gaConfigScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${process.env.ANALYTIC_KEY}', {
          send_page_view: true,
          page_title: document.title,
          page_location: window.location.href
        });
        window.gtag = gtag;
      `;
      document.head.appendChild(gaConfigScript);

      const customStyle = document.createElement("style");
      customStyle.innerHTML = `
        body.dark-mode {
          background: #18181B !important;
          color: #FAFAFA !important;
          transition: background-color 0.3s ease, color 0.3s ease !important;
        }
        body.light-mode {
          background: #FAFAFA !important;
          color: #18181B !important;
          transition: background-color 0.3s ease, color 0.3s ease !important;
        }
        input:focus, textarea:focus {
          outline: none !important;
          box-shadow: none !important;
        }
        .r-outline-0:focus {
          outline: none !important;
          box-shadow: none !important;
        }
        /* Smooth transitions for all theme-related changes */
        * {
          transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease !important;
        }
        /* Safari-specific fixes */
        input[type="text"], input[type="email"], textarea {
          -webkit-appearance: none;
          -webkit-border-radius: 0;
          border-radius: 8px;
        }
        /* Improve form handling in Safari */
        form {
          -webkit-font-smoothing: antialiased;
        }
      `;
      document.head.appendChild(customStyle);
    }
  }, []);

  if (!fontsLoaded && !fontError) {
    return null;
  }

  return (
    <ThemeProvider data-oid="vujp8lo">
      <AuthProvider data-oid="o1p52xy">
        <FavoritesProvider data-oid="4a.gra-">
          <AppNavigation data-oid="okzsqb6" />
        </FavoritesProvider>
      </AuthProvider>
      {Platform.OS === "web" && (
        <>
          <SpeedInsights data-oid="udqx.53" />
          <Analytics data-oid="gg4.w.n" />
        </>
      )}
    </ThemeProvider>
  );
}
